/**
 * Format help for the leads CSV import dialog.
 * Only a phone column is required. The download uses the signed-in session,
 * then falls back to the static template file.
 */
const COLUMNS = [
  {
    column: 'Phone',
    required: 'Required',
    detail: 'The number to call. Stored as E.164.',
    aliases: 'Phone, Phone Number, Mobile, Cell, Telephone, Tel, Number'
  },
  {
    column: 'Name',
    required: 'Optional',
    detail: 'The person’s name.',
    aliases: 'Name, Full Name, Contact, Contact Name, Lead Name. Or First Name and Last Name.'
  },
  {
    column: 'Company',
    required: 'Optional',
    detail: 'Business the person works for.',
    aliases: 'Company, Business, Business Name, Company Name, Organization'
  },
  {
    column: 'Email',
    required: 'Optional',
    detail: 'Email address.',
    aliases: 'Email, E-mail, Email Address'
  },
  {
    column: 'City',
    required: 'Optional',
    detail: 'City.',
    aliases: 'City, Town'
  },
  {
    column: 'State',
    required: 'Optional',
    detail: 'State or province.',
    aliases: 'State, ST, Province, Region'
  },
  {
    column: 'Industry',
    required: 'Optional',
    detail: 'Industry.',
    aliases: 'Industry, Vertical, Sector'
  },
  {
    column: 'Website',
    required: 'Optional',
    detail: 'Website.',
    aliases: 'Website, URL, Web'
  },
  {
    column: 'Notes',
    required: 'Optional',
    detail: 'Anything your team should see. “Stop calling”, “do not call”, or “remove me” marks that row do-not-call.',
    aliases: 'Notes, Note, Comments'
  },
  {
    column: 'consent_type',
    required: 'Optional',
    detail: 'Kind of consent: express_written, express, callback_request, inbound_inquiry, or existing_relationship.',
    aliases: 'Consent Type'
  },
  {
    column: 'consent_source',
    required: 'Optional',
    detail: 'Where it was collected: web_form, paper_form, recorded_verbal, import_attestation, callback_request, inbound_call, api, or other.',
    aliases: 'Consent Source'
  },
  {
    column: 'consent_captured_at',
    required: 'Optional',
    detail: 'When they consented, such as 2026-09-01T15:00:00Z.',
    aliases: 'Consent Captured At'
  },
  {
    column: 'consent_evidence',
    required: 'Optional',
    detail: 'A short note you can produce later, such as which form they filled out.',
    aliases: 'Consent Evidence'
  }
];

function apiBase() {
  if (typeof window !== 'undefined' && window.API_BASE) {
    return String(window.API_BASE).replace(/\/$/, '');
  }
  return '';
}

async function downloadTemplate(event) {
  if (event && event.preventDefault) event.preventDefault();
  const headers = {};
  try {
    const token = window.localStorage.getItem('authToken');
    if (token) headers.Authorization = 'Bearer ' + token;
    const res = await fetch(apiBase() + '/api/leads/import-template', {
      headers,
      credentials: 'include'
    });
    if (!res.ok) throw new Error('template');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'leads-import-template.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (_err) {
    const fallback = document.createElement('a');
    fallback.href = '/templates/leads-import-template.csv';
    fallback.download = 'leads-import-template.csv';
    document.body.appendChild(fallback);
    fallback.click();
    fallback.remove();
  }
}

export function LeadImportHelp() {
  return (
    <div className="import-preview lead-import-help">
      <p style={{ margin: '0 0 8px', fontSize: '0.8rem', fontWeight: 600 }}>
        Only a Phone column is required. Other columns are optional.
      </p>
      <div className="import-preview-table-wrapper" style={{ maxHeight: 240 }}>
        <table className="table table-compact">
          <thead>
            <tr>
              <th>Column</th>
              <th>Required</th>
              <th>Description</th>
              <th>Also accepted</th>
            </tr>
          </thead>
          <tbody>
            {COLUMNS.map((row) => (
              <tr key={row.column}>
                <td>{row.column}</td>
                <td>{row.required}</td>
                <td>{row.detail}</td>
                <td>{row.aliases}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ margin: '8px 0 0', fontSize: '0.75rem', color: 'var(--muted)' }}>
        Phone numbers can look like (555) 123-4567, 555-123-4567, or +15551234567. A 10-digit number is treated as a US number.
      </p>
      <p style={{ margin: '8px 0 0', fontSize: '0.75rem', color: 'var(--muted)' }}>
        If you have written consent from these people to receive AI calls (for example a web form they filled out), fill in the consent columns. Without consent, the AI can only call verified business landlines; your team can still call anyone by hand.
      </p>
      <p style={{ margin: '8px 0 0', fontSize: '0.75rem', color: 'var(--muted)' }}>
        Use a .csv file up to 5 MB. Rows with an invalid phone, a number already in this workspace, or a number on your do-not-call list are skipped.
      </p>
      <a
        href="/templates/leads-import-template.csv"
        download="leads-import-template.csv"
        className="button small outline"
        style={{ marginTop: 8, display: 'inline-block' }}
        onClick={downloadTemplate}
      >
        Download template
      </a>
    </div>
  );
}
